import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaGlobe,
    FaHandshake,
    FaLinkedin,
    FaQuoteLeft,
    FaRocket,
    FaShieldAlt,
    FaTwitter
} from "react-icons/fa";
import {
    TiArrowRight,
    TiArrowUp,
    TiChevronRight,
    TiFlash,
    TiLightbulb,
    TiLocationArrow
} from "react-icons/ti";
import { useNavigate } from "react-router-dom";
import AnimatedTitle from "./AnimatedTitle";
import CorePrincipleModal from "./CorePrincipleModal";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from "./ui/GlassButton";
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const AboutUs = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const [selectedPrinciple, setSelectedPrinciple] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);
  const timelineRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    // Staggered entrance for sections
    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            delay: index * 0.1,
            scrollTrigger: {
              trigger: section,
              start: "top 85%",
            },
          }
        );
      }
    });

    // Timeline line animation
    if (timelineRef.current) {
      gsap.fromTo(
        ".timeline-line",
        { height: 0 },
        {
          height: "100%",
          duration: 2,
          scrollTrigger: {
            trigger: timelineRef.current,
            start: "top 80%",
            end: "bottom 20%",
            scrub: 1,
          },
        }
      );
    }

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const stats = [
    { value: "8+", label: "Years in Web3", icon: <TiFlash className="text-cyan-400" /> },
    { value: "21+", label: "Products Launched", icon: <FaRocket className="text-purple-400" /> },
    { value: "50+", label: "Global Partners", icon: <FaHandshake className="text-emerald-400" /> },
    { value: "100%", label: "Decentralised Focus", icon: <FaShieldAlt className="text-orange-400" /> },
  ];

  const coreValues = [
    {
      title: "Integrity First",
      desc: "Transparency is our foundation. We believe in open-source progress and immutable trust.",
      icon: <FaShieldAlt />,
      color: "cyan"
    },
    {
      title: "Radical Innovation",
      desc: "We don't follow trends; we set them. Engineering solutions for the next decade of finance.",
      icon: <TiLightbulb />,
      color: "purple"
    },
    {
      title: "Global Inclusivity",
      desc: "Blockchain has no borders. Our products are designed for everyone, everywhere.",
      icon: <FaGlobe />,
      color: "emerald"
    }
  ];

  const timelineEvents = [
    { year: "2017", title: "The Genesis", desc: "Matt and Chris Collins founded Zero-Blockchain in London with a vision to democratise finance." },
    { year: "2019", title: "Infrastructure Growth", desc: "Launch of Zero-Core and the first enterprise node-as-a-service prototype." },
    { year: "2021", title: "The Ecosystem Boom", desc: "Rapid expansion into DeFi with Zero-Dex and Zero-Lend protocols reaching $100M+ TVL." },
    { year: "2023", title: "AI Integration", desc: "Pioneered AI-driven predictive analytics for institutional blockchain management." },
    { year: "2025", title: "Global Scaling", desc: "Establishing regional hubs in Miami and Singapore to support 1M+ active ecosystem users." },
  ];

  const team = [
    { 
      name: "Matt Collins", 
      role: "CEO & Founder", 
      image: "/assets/images/about/team1.webp",
      bio: "Strategic visionary with 12+ years in quantitative finance and blockchain architecture.",
      linkedin: "#",
      twitter: "#"
    },
    { 
      name: "Chris Collins", 
      role: "CFO & Co-Founder", 
      image: "/assets/images/about/team2.webp",
      bio: "Former investment banker focused on sustainable tokenomics and institutional compliance.",
      linkedin: "#",
      twitter: "#"
    },
    { 
      name: "Kevin King", 
      role: "Head of Development", 
      image: "/assets/images/about/team3.webp",
      bio: "Full-stack lead specializing in high-performance smart contract auditing and ZK-rollups.",
      linkedin: "#",
      twitter: "#"
    },
  ];

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/about-us-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="aboutHeroSparkles"
              background="transparent"
              minSize={0.4}
              maxSize={1.0}
              particleDensity={30}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Building the Future of Web3"
              containerClass="text-center text-blue-50 font-zentry relative z-10 px-4"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Powered by ZC-1 Protocol · PoS + BFT + DAG
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-16 py-20 mx-auto max-w-7xl">
        
        {/* Intro & Stats */}
        <div className="grid lg:grid-cols-2 gap-16 mb-32 items-center" ref={(el) => (sectionRefs.current[0] = el)}>
          <div>
            <motion.span 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-bold uppercase tracking-widest mb-6 inline-block"
            >
              Our Mission
            </motion.span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 leading-tight">
              Decentralising the <span className="text-cyan-400 font-zentry italic">World's Assets</span>
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Founded in 2017, Zero-Blockchain began with a simple but radical premise: 
              that financial systems should be open, transparent, and accessible to everyone. 
              We've spent the last 8 years building the infrastructure to make that a reality.
            </p>
            <p className="text-gray-500 italic flex gap-4 items-start">
              <FaQuoteLeft className="text-cyan-500 shrink-0 mt-1" />
              "We're not just building products; we're building a new layer of trust for the global internet economy. Zero is our standard, and Blockchain is our foundation."
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4 md:gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 bg-white/5 border border-white/10 rounded-3xl text-center group hover:border-cyan-500/30 transition-all duration-300"
              >
                <div className="flex justify-center text-3xl mb-4 transform group-hover:scale-110 transition-transform duration-300">{stat.icon}</div>
                <div className="text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-[10px] text-gray-500 uppercase tracking-widest">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Vision & Values Bento */}
        <div className="mb-32" ref={(el) => (sectionRefs.current[1] = el)}>
          <div className="text-center mb-16">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">Core Principles</h3>
            <p className="text-gray-300 max-w-2xl mx-auto">The values that guide every line of code we write and every partnership we form.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {coreValues.map((val, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -10 }}
                className="relative p-10 bg-black/50 backdrop-blur-sm border border-white/10 rounded-[2.5rem] overflow-hidden group transition-all duration-500 flex flex-col justify-between min-h-[450px]"
              >
                <div>
                  {/* Spotlight effect */}
                  <div className={`absolute -top-24 -right-24 w-48 h-48 bg-gradient-radial from-${val.color}-500/20 to-transparent rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                  
                  <div className={`w-16 h-16 rounded-2xl bg-${val.color}-500/10 flex items-center justify-center text-3xl text-${val.color}-400 mb-8 border border-white/5`}>
                    {val.icon}
                  </div>
                  <h4 className="text-2xl font-bold text-white mb-4">{val.title}</h4>
                  <p className="text-gray-300 leading-relaxed mb-6">{val.desc}</p>
                </div>

                <div className="mt-auto">
                  <GlassButton
                    onClick={() => {
                      setSelectedPrinciple(val);
                      setIsModalOpen(true);
                    }}
                    variant="gray"
                    className="w-full py-4"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold uppercase tracking-wider">Learn More</span>
                      <TiArrowRight className="text-xl" />
                    </div>
                  </GlassButton>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Heritage Timeline */}
        <div className="mb-32" ref={(el) => (sectionRefs.current[2] = el)}>
          <div className="text-center mb-24">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">Our Heritage</h3>
            <p className="text-gray-300">A journey of innovation, from a small London office to a global powerhouse.</p>
          </div>

          <div className="relative max-w-5xl mx-auto" ref={timelineRef}>
            {/* Central Line */}
            <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-white/10 hidden md:block">
              <div className="timeline-line w-full bg-gradient-to-b from-cyan-500 via-purple-500 to-emerald-500 origin-top" />
            </div>

            <div className="space-y-24">
              {timelineEvents.map((event, index) => (
                <div key={index} className="relative">
                  {/* Timeline Dot */}
                  <div className="absolute left-1/2 -translate-x-1/2 top-2 w-4 h-4 rounded-full bg-black border-2 border-cyan-500 z-10 hidden md:block group-hover:scale-150 transition-transform shadow-[0_0_10px_rgba(34,211,238,0.5)]" />
                  
                  <div className={`flex flex-col md:flex-row items-center gap-8 md:gap-0 ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>
                    <div className={`w-full md:w-1/2 px-4 md:px-16 text-center ${index % 2 === 0 ? 'md:text-left' : 'md:text-right'}`}>
                      <div className={`text-4xl font-zentry italic font-bold mb-2 ${index % 2 === 0 ? 'md:text-left' : 'md:text-right'} bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent`}>
                        {event.year}
                      </div>
                      <h4 className={`text-xl font-bold text-white mb-4 ${index % 2 === 0 ? 'md:text-left' : 'md:text-right'}`}>{event.title}</h4>
                      <p className={`text-gray-300 leading-relaxed ${index % 2 === 0 ? 'md:text-left' : 'md:text-right'}`}>{event.desc}</p>
                    </div>
                    <div className="hidden md:block w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Team Section */}
        <div className="mb-32" ref={(el) => (sectionRefs.current[3] = el)}>
          <div className="text-center mb-16">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">The Leadership</h3>
            <p className="text-gray-300">Meet the pioneers engineering the future of finance.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 px-4">
            {team.map((member, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -10 }}
                className="group relative"
              >
                <div className="absolute -inset-[1px] rounded-[2.5rem] bg-gradient-to-b from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative p-1 bg-black rounded-[2.5rem] overflow-hidden border border-white/5 group-hover:border-white/20 transition-all duration-500">
                  {/* Image Container */}
                  <div className="h-80 w-full overflow-hidden rounded-[2rem]">
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700"
                    />
                  </div>
                  
                  {/* Info Overlay */}
                  <div className="p-8">
                    <h4 className="text-2xl font-bold text-white mb-1">{member.name}</h4>
                    <p className="text-cyan-400 text-sm font-bold uppercase tracking-widest mb-4">{member.role}</p>
                    <p className="text-gray-300 text-sm leading-relaxed mb-6">{member.bio}</p>
                    
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-4">
                        <a href={member.linkedin} className="text-white/40 hover:text-white transition-colors text-xl"><FaLinkedin /></a>
                        <a href={member.twitter} className="text-white/40 hover:text-white transition-colors text-xl"><FaTwitter /></a>
                      </div>
                      <div className="ml-auto">
                        <GlassButton
                          onClick={() => window.open(member.linkedin, '_blank')}
                          variant="gray"
                          size="sm"
                          className="px-4 py-2"
                        >
                          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider">
                            Profile <TiChevronRight />
                          </div>
                        </GlassButton>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative rounded-[3rem] overflow-hidden border border-white/10"
          ref={(el) => (sectionRefs.current[4] = el)}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 via-purple-500/20 to-transparent" />
          <div className="absolute inset-0 bg-black/60 backdrop-blur-xl" />
          
          <div className="relative z-10 py-24 px-8 text-center">
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Be Part of our <span className="font-zentry italic text-cyan-400">Journey</span>
            </h3>
            <p className="text-gray-300 max-w-2xl mx-auto mb-12 text-lg">
              We're always looking for talented developers, visionary partners, and ambitious 
              institutions to help us build a more decentralised world.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <GlassButton
                onClick={() => navigate('/services')}
                variant="cyan"
                className="px-10 py-5"
              >
                <div className="flex items-center gap-3 text-xl font-bold">
                  Partner with Us
                  <TiLocationArrow className="text-2xl" />
                </div>
              </GlassButton>
              
              <GlassButton
                onClick={() => navigate('/support')}
                variant="gray"
                className="px-10 py-5"
              >
                <div className="flex items-center gap-3 text-xl font-bold">
                  Contact the Team
                  <TiArrowRight className="text-2xl" />
                </div>
              </GlassButton>
            </div>
            
            <div className="mt-16 flex flex-wrap justify-center gap-12 text-gray-500">
              <div className="flex items-center gap-2"><FaGlobe className="text-cyan-500" /> London HQ</div>
              <div className="flex items-center gap-2"><FaGlobe className="text-purple-500" /> Singapore Hub</div>
              <div className="flex items-center gap-2"><FaGlobe className="text-emerald-500" /> Miami Office</div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Back to Top Button */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-cyan-500/30 rounded-full shadow-lg shadow-cyan-500/10 cursor-pointer hover:bg-black/60 hover:border-cyan-400/50 transition-all duration-500 z-50"
          onClick={scrollToTop}
          aria-label="Back to Top"
        >
          <TiArrowUp className="text-cyan-400 transition-all duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider transition-colors duration-500 hover:text-white">
            BACK TO TOP
          </span>
        </div>
      )}

      {/* Footer */}
      <Footer />

      {/* Principle Modal */}
      <CorePrincipleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        principle={selectedPrinciple}
      />
    </div>
  );
};

export default AboutUs;
